import {useEffect, useSyncExternalStore} from 'react';
import Icon from '../components/Icon';
import {dismissToast, makeToast, pushToast, Toast, ToastInput} from '../lib/toastQueue';

// A module-level store so anything (upload queue, mutation cache, a page) can show a toast:
//   toast.success('Saved');  toast.problem('Photo didn't upload', {action: {label: 'Retry', onClick}})
// <ToastViewport /> is mounted once in App and renders the queue above the tab bar.

let queue: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<number, number>();

const emit = () => listeners.forEach(l => l());

const dismiss = (id: number) => {
    const timer = timers.get(id);
    if (timer !== undefined) {
        window.clearTimeout(timer);
        timers.delete(id);
    }
    queue = dismissToast(queue, id);
    emit();
};

const show = (input: ToastInput): number => {
    const t = makeToast(input);
    queue = pushToast(queue, t);
    emit();
    if (!t.sticky) {
        timers.set(t.id, window.setTimeout(() => dismiss(t.id), t.duration));
    }
    return t.id;
};

export const toast = {
    show,
    dismiss,
    info: (message: string, opts: Omit<ToastInput, 'message' | 'variant'> = {}) => show({...opts, message, variant: 'info'}),
    success: (message: string, opts: Omit<ToastInput, 'message' | 'variant'> = {}) => show({...opts, message, variant: 'success'}),
    problem: (message: string, opts: Omit<ToastInput, 'message' | 'variant'> = {}) => show({...opts, message, variant: 'problem'})
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const useToasts = () => useSyncExternalStore(subscribe, () => queue, () => queue);

const VARIANT_CLASS: Record<Toast['variant'], string> = {
    info: 'bg-ink text-ground',
    success: 'bg-shoot-soft text-shoot-text',
    problem: 'bg-rust-soft text-rust'
};

export function ToastViewport() {
    const toasts = useToasts();

    // Clear any timers if the viewport unmounts (tests, hot reload).
    useEffect(() => () => timers.forEach(t => window.clearTimeout(t)), []);

    if (toasts.length === 0) return null;

    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-[100px] z-40 flex flex-col items-center gap-2 px-4">
            {toasts.map(t => (
                <div
                    key={t.id}
                    role={t.variant === 'problem' ? 'alert' : 'status'}
                    className={`settle-in pointer-events-auto flex w-full max-w-[528px] items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold shadow-float ${VARIANT_CLASS[t.variant]}`}
                >
                    <span className="min-w-0 flex-1 leading-snug">{t.message}</span>
                    {t.action && (
                        <button
                            type="button"
                            className="shrink-0 rounded-sm px-2 py-1 font-display text-sm font-bold underline-offset-2 hover:underline"
                            onClick={() => {
                                dismiss(t.id);
                                t.action?.onClick();
                            }}
                        >
                            {t.action.label}
                        </button>
                    )}
                    <button type="button" aria-label="Dismiss" className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-pill opacity-70" onClick={() => dismiss(t.id)}>
                        <Icon name="close" size={16} strokeWidth={2.2} />
                    </button>
                </div>
            ))}
        </div>
    );
}
